import React from "react";
import { Tooltip, IconButton, CircularProgress } from "@mui/material";
import { Download } from "@mui/icons-material";
import moment from "moment";
import { exportExcelFile } from "utils/exportExcelFile";
import { useLazyGetShipmentPricingReportQuery } from "store/services/reportApi";
import { useAuth } from "hooks";

const getShipmentUnit = (shipment) => {
  if (shipment?.weight !== undefined && shipment?.weight !== null) {
    return { value: shipment.weight, unit: "kg" };
  }

  const unitValue = shipment?.unit?.value;
  const unitName = shipment?.unit?.unitType?.name;
  if (unitValue !== undefined && unitValue !== null && unitName) {
    return {
      value: unitValue,
      unit: unitName.toLowerCase() === "weight" ? "kg" : unitName,
    };
  }

  return { value: "N/A", unit: "N/A" };
};

const ExportShipmentPricingReport = ({
  timeRange = "last7days",
  customFromDate = null,
  customToDate = null,
  keyword = "",
  region = "all",
  branch = "all",
  payer = "all",
  user = "all",
  responsibleUser = "all",
  customer = "all",
  vendor = "all",
  relationshipManager = "all",
  paymentStatus = "all",
  disabled = false,
}) => {
  const [downloading, setDownloading] = React.useState(false);
  const [fetchPricingReport] = useLazyGetShipmentPricingReportQuery();
  const { viewEntity } = useAuth();


  const computeParams = () => {
    const today = moment().startOf("day");
    let fromDate;
    let toDate;

    switch (timeRange) {
      case "today":
        fromDate = today.format("MM-DD-YYYY");
        toDate = today.endOf("day").format("MM-DD-YYYY");
        break;
      case "last7days":
        fromDate = moment()
          .subtract(6, "days")
          .startOf("day")
          .format("MM-DD-YYYY");
        toDate = today.format("MM-DD-YYYY");
        break;
      case "oneMonth":
        fromDate = moment()
          .subtract(1, "month")
          .startOf("day")
          .format("MM-DD-YYYY");
        toDate = today.format("MM-DD-YYYY");
        break;
      case "custom":
        fromDate = customFromDate
          ? moment(customFromDate).format("MM-DD-YYYY")
          : undefined;
        toDate = customToDate
          ? moment(customToDate).format("MM-DD-YYYY")
          : undefined;
        if (!fromDate || !toDate) return null;
        break;
      case "all":
      default:
        fromDate = undefined;
        toDate = undefined;
        break;
    }

    return {
      ...(fromDate && { fromDate }),
      ...(toDate && { toDate }),
      ...(keyword && { keyword }),
      ...(region && region !== "all" && { region }),
      ...(branch && branch !== "all" && { branch }),
      ...(payer && payer !== "all" && { payer }),
      ...(user && user !== "all" && { user }),
      ...(responsibleUser && responsibleUser !== "all" && { responsibleUser }),
      ...(customer && customer !== "all" && { customer }),
      ...(vendor && vendor !== "all" && { vendor }),
      ...(relationshipManager &&
        relationshipManager !== "all" && { relationshipManager }),
      ...(paymentStatus && paymentStatus !== "all" && { paymentStatus }),
    };
  };

  const handleDownload = async () => {
    const baseParams = computeParams();
    if (!baseParams) return;

    setDownloading(true);
    try {
      const pageSize = 1000;

      const first = await fetchPricingReport({
        ...baseParams,
        page: 1,
        limit: pageSize,
        account: viewEntity?.entity?.account?._id,
        entity: viewEntity?.entity?._id,

      }).unwrap();

      const totalItems = Number(first?.totalItems || 0);
      const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
      let allRows = Array.isArray(first?.data) ? [...first.data] : [];

      for (let p = 2; p <= totalPages; p += 1) {
        const res = await fetchPricingReport({
          ...baseParams,
          page: p,
          limit: pageSize,
        }).unwrap();
        if (Array.isArray(res?.data)) allRows = allRows.concat(res.data);
      }

      const sheetData = allRows.map((row) => {
        const shipmentUnit = getShipmentUnit(row?.shipment);

        return {
          ShipmentID: row?.shipment?.shipmentId || "",
          ShipmentStatus: row?.shipment?.status || "",
          DeliveryDate: row?.deliveryDate || "",
          FromBranch: row?.sourceBranch?.name || "",
          FromProvince: row?.sourceBranch?.region?.name || "",
          ToBranch: row?.destinationBranch?.name || "",
          ToProvince: row?.destinationBranch?.region?.name || "",
          Sender: row?.sender || "",
          RelationshipManager: row?.relationshipManager || "",
          Receiver: row?.receiver || "",
          Payer: row?.payer || "",
          Charge: row?.charge ?? 0,
          FeeItem: row?.feeItem?.name || "",
          UnitValue: shipmentUnit.value,
          Unit: shipmentUnit.unit,
          PaymentBranch: row?.paymentBranch || "",
          PaymentLocation: row?.paymentLocation || "",
          CreatedBy: row?.createdBy?.name || "",
          ResponsibleUser: row?.responsibleUser?.name || "",
          PaymentStatus: row?.paymentStatus || "",
          CreatedAt: row?.createdAt ? moment(row.createdAt).format("MMM DD, YYYY HH:mm") : "",
        };
      });

      exportExcelFile({ sheetData, fileName: "shipment-pricing-report" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Download failed:", err);
    } finally {
      setDownloading(false);
    }
  };

  const isDisabled =
    disabled ||
    downloading ||
    (timeRange === "custom" && (!customFromDate || !customToDate));

  return (
    <Tooltip title={downloading ? "Exporting..." : "Download Excel"}>
      <span>
        <IconButton
          onClick={handleDownload}
          color="primary"
          disabled={isDisabled}
          aria-busy={downloading}
        >
          {downloading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <Download />
          )}
        </IconButton>
      </span>
    </Tooltip>
  );
};

export default ExportShipmentPricingReport;
